import React from 'react';
import DatePicker from '../../DatePicker';
import { clinicNow } from '../../../utils/clinicTime';

// The date-range bar every ranged report shares: preset chips plus a custom from/to.
// Presets are anchored to the clinic's "today", not the browser's, so a report pulled up from a
// different timezone still describes the clinic's day. There is deliberately no "All time":
// the backend caps ranges at 1000 days, and an unbounded report is a slow report.

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const getPresetRange = (preset) => {
  const today = clinicNow();
  today.setHours(0, 0, 0, 0);
  const from = new Date(today);
  const to = new Date(today);
  switch (preset) {
    case 'today':
      break;
    case 'yesterday':
      from.setDate(from.getDate() - 1);
      to.setDate(to.getDate() - 1);
      break;
    case 'last7':
      from.setDate(from.getDate() - 6);
      break;
    case 'last30':
      from.setDate(from.getDate() - 29);
      break;
    case 'thisMonth':
      from.setDate(1);
      break;
    case 'lastMonth':
      from.setMonth(from.getMonth() - 1);
      from.setDate(1);
      to.setDate(0);
      break;
    case 'last6Months':
      from.setMonth(from.getMonth() - 6);
      break;
    case 'thisYear':
      from.setMonth(0);
      from.setDate(1);
      break;
    default:
      break;
  }
  return { from: toISO(from), to: toISO(to) };
};

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7', label: 'Last 7 Days' },
  { key: 'last30', label: 'Last 30 Days' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'last6Months', label: 'Last 6 Months' },
  { key: 'thisYear', label: 'This Year' },
];

/**
 * @param value    { from, to } ISO strings
 * @param preset   active preset key, or 'custom'
 * @param onChange ({ from, to }, presetKey) - fired for presets and custom edits alike
 * @param presets  optional subset of preset keys to offer
 */
const DateRangeFilter = ({ value, preset, onChange, presets }) => {
  const offered = presets ? PRESETS.filter((p) => presets.includes(p.key)) : PRESETS;
  return (
    <div className="bg-white rounded-2xl shadow p-4 flex flex-wrap items-center gap-2">
      {offered.map((p) => (
        <button
          key={p.key}
          onClick={() => onChange(getPresetRange(p.key), p.key)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            preset === p.key
              ? 'bg-navy-800 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {p.label}
        </button>
      ))}
      <div className="flex items-center gap-2 ml-auto">
        <span className={`text-xs font-semibold ${preset === 'custom' ? 'text-emerald-700' : 'text-gray-500'}`}>
          Custom
        </span>
        <DatePicker
          value={value.from}
          max={value.to || undefined}
          onChange={(v) => onChange({ from: v, to: value.to }, 'custom')}
          placeholder="From"
        />
        <span className="text-gray-400 text-sm">to</span>
        <DatePicker
          value={value.to}
          min={value.from || undefined}
          onChange={(v) => onChange({ from: value.from, to: v }, 'custom')}
          placeholder="To"
        />
      </div>
    </div>
  );
};

export default DateRangeFilter;
